/* eslint-disable no-console */
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import { Box, styled, Typography, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { FlexPoolOnlyIndicator } from 'shared/components/common/flexpool-only-indicator';
import { useMemo, useRef, useState } from 'react';
import { useOgpGetShiftPopoverDetail } from 'shared/components/shift-forms/hooks/ogp/use-ogp-get-shift-popover-details';
import { useSiteDetection } from 'shared/hooks';
import { getFlexWorkerLabel } from 'shared/utils/get-flex-worker-label';
import { ShiftPopover } from './shift-details-popper';
import { usePlanningParamsContext } from '../../../../providers/planning-params-provider';

type ShiftData = {
  shiftData: {
    applicants: number;
    claimants: number;
    jobName: string;
    flexPoolOnly: boolean;
    dateLabel: string;
    dateTime: string;
    endTime: string;
    id: string;
    maxClaimants: number;
    startTime: string;
    hasCanceledClaim: boolean;
  };
};

const defaultCapacityBoxColor = '#F5FAFF';
const debounceDelay = 500;

export const CalendarShiftItem = ({ shiftData }: ShiftData) => {
  const theme = useTheme();
  const { site } = useSiteDetection();

  const { setFilters } = usePlanningParamsContext();
  const colorData = useMemo(() => getColor({ shiftData }, theme), [shiftData, theme]);

  const [shouldFetchDetails, setShouldFetchDetails] = useState(false);
  const { data, isSuccess } = useOgpGetShiftPopoverDetail(site, shiftData.id, shouldFetchDetails);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveredRef = useRef<boolean>(false);

  const popoverData = useMemo(() => {
    if (!isSuccess || !data) {
      return null;
    }

    return {
      id: data.id,
      name: data.name,
      maxClaimants: data.maxClaimants,
      acceptedClaimantsCount: data.claimants.length,
      flexpoolClaimantsCount: data.flexPools.length,
      claimants: data.claimants.map((claimant) => ({
        ...claimant,
        label:
          getFlexWorkerLabel({
            employmentType: claimant.label.workerType,
          }) ?? '',
        imageUrl: claimant.imageUrl ?? undefined,
      })),
    };
  }, [isSuccess, data]);

  const startHoverEffect = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    isHoveredRef.current = true;
    const target = e.currentTarget;
    hoverTimerRef.current = setTimeout(() => {
      if (isHoveredRef.current) {
        setShouldFetchDetails(true);
        setAnchorEl(target);
      }
    }, debounceDelay);
  };

  const stopHoverEffect = () => {
    isHoveredRef.current = false;
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setShouldFetchDetails(false);
    setAnchorEl(null);
  };

  if (!shiftData) {
    return <EmptyShift item />;
  }
  const isHistorical = DateTime.fromISO(shiftData.dateTime) < DateTime.now();
  const ShiftWrapperComponent = isHistorical ? HistoricalShiftWrapper : RegularShiftWrapper;

  return (
    <ShiftWrapperComponent data-testid={`shift-${shiftData.id}`} item>
      <ShiftBox
        onClick={() => {
          setFilters({
            shiftId: shiftData.id,
          });
        }}
        backgroundColor={colorData.cardColor}
        data-testid={`shift-box-${shiftData.id}`}
        onMouseOver={(e) => startHoverEffect(e)}
        onMouseLeave={stopHoverEffect}
      >
        <ShiftJobTitleBox>
          <JobNameTypography variant="subtitle2" noWrap>
            {shiftData.jobName}
          </JobNameTypography>
        </ShiftJobTitleBox>

        <ShiftTimeBox>
          <ShiftTimeTypography variant="subtitle2">
            {shiftData.startTime} - {shiftData.endTime}
          </ShiftTimeTypography>
          <FlexPoolOnlyIndicator flexPoolOnly={shiftData.flexPoolOnly} />
        </ShiftTimeBox>
        <CapacityBox
          backgroundColor={colorData.capacityBoxColor}
          data-testid={`capacity-box-${shiftData.id}`}
        >
          <ClaimantsTypography variant="body2">
            <ClaimantsValue>{shiftData.claimants}</ClaimantsValue>
            {shiftData.hasCanceledClaim ? (
              <RedDot component="span" data-testid={`claimants-red-dot-${shiftData.id}`}></RedDot>
            ) : null}
            <MaxClaimants>
              {' / '}
              {shiftData.maxClaimants}
            </MaxClaimants>
          </ClaimantsTypography>
          <BadgeBox
            backgroundColor={colorData.badgeColor}
            data-testid={`shift-badge-${shiftData.id}`}
          >
            <Typography variant="body2">{shiftData.applicants}</Typography>
          </BadgeBox>
        </CapacityBox>
      </ShiftBox>
      <ShiftPopover
        open={shouldFetchDetails && isSuccess && !!data}
        anchorEl={anchorEl}
        data={popoverData}
        maxClaimants={data?.maxClaimants}
      />
    </ShiftWrapperComponent>
  );
};

const EmptyShift = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  backgroundColor: 'transparent',
  height: theme.spacing(4),
}));

const RegularShiftWrapper = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  opacity: 1,
}));

const HistoricalShiftWrapper = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  opacity: 0.5,
}));

const ShiftBox = styled(Box)<{ backgroundColor: string }>(({ theme, backgroundColor }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  backgroundColor,
  padding: theme.spacing(2),
  width: '100%',
  height: '10rem',
  flexShrink: 0,
  borderRadius: '1rem',
  boxShadow:
    '0px 4px 6px 0px rgba(51, 51, 51, 0.03), 0px 0px 8px 0px rgba(51, 51, 51, 0.03), 0px 12px 16px 0px rgba(51, 51, 51, 0.08)',
  transition: '0.5s',
  '&:hover': {
    boxShadow: '0px 12px 16px rgba(51, 51, 51, 0.16), 0px 4px 6px rgba(51, 51, 51, 0.08)',
  },
}));

const CapacityBox = styled(Box)<{ backgroundColor: string }>(({ backgroundColor }) => ({
  backgroundColor,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexDirection: 'row',
  borderRadius: '0.75rem',
  padding: '1rem 0.75rem',
}));

const ShiftJobTitleBox = styled(Box)({
  padddingTop: '0.5rem',
  paddingBottom: '0.5rem',
  display: 'flex',
  justifyContent: 'flex-start',
});

const ShiftTimeBox = styled(Box)({
  display: 'flex',
  height: '2rem',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  paddingBottom: '0.5rem',
  alignItems: 'center',
});

const JobNameTypography = styled(Typography)(({ theme }) => ({
  width: '100%',
  fontWeight: 600,
  fontSize: '14px',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),

  textOverflow: 'ellipsis',
  whiteSpace: 'normal',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
}));

const ShiftTimeTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '14px',
  letterSpacing: '.3px',
  gap: theme.spacing(2),
  whiteSpace: 'wrap',
  overflow: 'hidden',
}));

const ClaimantsTypography = styled(Typography)({
  fontWeight: 700,
  fontSize: '14px',
  display: 'flex',
});

const ClaimantsValue = styled(Box)(({ theme }) => ({
  color: theme.palette.lwBlack['100'],
}));

const MaxClaimants = styled(Box)(({ theme }) => ({
  color: theme.palette.lwBlack['60'],
}));

const BadgeBox = styled(Box)<{ backgroundColor: string }>(({ theme, backgroundColor }) => ({
  height: theme.spacing(6),
  width: theme.spacing(6),
  backgroundColor,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: '1px',
  '& .MuiTypography-root': {
    color: 'white',
  },
}));

const RedDot = styled(Box)(({ theme }) => ({
  borderRadius: '50%',
  height: '6px',
  width: '6px',
  backgroundColor: theme.palette.lwDanger['100'],
  display: 'inline-block',
  verticalAlign: 'super',
}));

const getColor = ({ shiftData }: ShiftData, theme: Theme) => {
  if (shiftData) {
    if (DateTime.fromISO(shiftData.dateTime) < DateTime.now()) {
      return {
        cardColor: theme.palette.lwBlack['20'],
        capacityBoxColor: defaultCapacityBoxColor,
        badgeColor: theme.palette.lwBlack['60'],
      };
    }
    if (shiftData.claimants === shiftData.maxClaimants) {
      return {
        cardColor: theme.palette.lwWhite['100'],
        capacityBoxColor: theme.palette.lwSuccess['20'],
        badgeColor: theme.palette.lwSuccess['100'],
      };
    }
    if (shiftData.applicants === 0) {
      return {
        cardColor: theme.palette.lwWhite['100'],
        capacityBoxColor: defaultCapacityBoxColor,
        badgeColor: theme.palette.lwDanger['60'],
      };
    }
    if (shiftData.applicants > 0) {
      return {
        cardColor: theme.palette.lwWhite['100'],
        capacityBoxColor: theme.palette.lwPrimaryAccentLight['10'],
        badgeColor: theme.palette.lwPrimaryAccentLight['100'],
      };
    }
  }
  return {
    cardColor: theme.palette.lwWhite['100'],
    capacityBoxColor: defaultCapacityBoxColor,
    badgeColor: theme.palette.lwBlack['100'],
  };
};
